/**
 * services/web-ui/src/api/voice-sessions-list.ts
 *
 * W4-C: API client wrappers for the voice session list + stats endpoints.
 * Counterpart to voice-sessions.ts (W4-B) which handles per-call detail.
 */

const BASE_URL = "/api";

class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const url = `${BASE_URL}${path}`;
  const res = await fetch(url, {
    ...options,
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
  });

  if (res.status === 401) {
    throw new ApiError(401, "Unauthorized");
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: res.statusText }));
    throw new ApiError(res.status, (body as { error?: string }).error ?? "Request failed");
  }

  return res.json() as Promise<T>;
}

// ─── Types ─────────────────────────────────────────────────────────────────────

export interface SessionListItem {
  /** UUID — primary key of voice_sessions */
  id: string;
  direction: "inbound" | "outbound";
  channel: string | null;
  call_sid: string | null;
  caller_number: string | null;
  callee_number: string | null;
  caller_person_id: string | null;
  status: "starting" | "active" | "ended" | "failed" | "dropped";
  started_at: string;
  ended_at: string | null;
  duration_seconds: number | null;
  task_outcome: "success" | "failure" | "incomplete" | "none" | null;
  current_stage: string | null;
  stages_visited: string[] | null;
  stages_skipped: string[] | null;
  workflow_id: string | null;
  workflow_version: number | null;
  /** Derived: anomaly_count = stages_skipped.length (computed client-side) */
  anomaly_count?: number;
}

export interface SessionListResponse {
  sessions: SessionListItem[];
  total: number;
  limit: number;
  offset: number;
}

/** Actual shape returned by GET /api/voice/sessions/stats */
export interface SessionStats {
  total_sessions: number;
  active_sessions: number;
  ended_sessions: number;
  failed_sessions: number;
  successful_outcomes: number;
  failed_outcomes: number;
  incomplete_outcomes: number;
  avg_duration_seconds: number | null;
  avg_ended_duration_seconds: number | null;
  last_call_at: string | null;
}

export interface SessionListParams {
  limit?: number;
  offset?: number;
  status?: string;
}

// ─── API functions ─────────────────────────────────────────────────────────────

export async function fetchSessions(params: SessionListParams = {}): Promise<SessionListResponse> {
  const qs = new URLSearchParams();
  if (params.limit != null) qs.set("limit", String(params.limit));
  if (params.offset != null) qs.set("offset", String(params.offset));
  if (params.status) qs.set("status", params.status);
  const query = qs.toString();

  const resp = await request<SessionListResponse>(`/voice/sessions${query ? `?${query}` : ""}`);

  // Derive anomaly_count client-side from stages_skipped length
  return {
    ...resp,
    sessions: resp.sessions.map((s) => ({
      ...s,
      anomaly_count: Array.isArray(s.stages_skipped) ? s.stages_skipped.length : 0,
    })),
  };
}

export async function fetchStats(): Promise<SessionStats> {
  return request<SessionStats>("/voice/sessions/stats");
}
